import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle, XCircle, MinusCircle, Trophy } from "@phosphor-icons/react";

export default function Results() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/results/summary")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 text-[#A1A1AA]" data-testid="results-loading">
        Loading results…
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 text-center" data-testid="results-empty">
        <Trophy size={32} color="#52525b" />
        <div className="text-[#A1A1AA] mt-4 mb-2">No graded picks yet — results appear as games finish.</div>
        <Link to="/" className="overline text-[#FF4500] hover:underline" data-testid="results-cta">
          Back to the board →
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <div className="overline mb-2">Last 48 Hours</div>
      <div className="flex items-baseline justify-between border-b border-[#27272A] pb-6 mb-8 flex-wrap gap-3">
        <h1 className="font-head text-4xl text-white tracking-tight">RESULTS</h1>
        <div className="flex items-center gap-6">
          <Stat label="Accuracy" value={`${data.accuracy}%`} accent="#FF4500" testid="results-accuracy" />
          <Stat label="Wins" value={data.wins} accent="#22c55e" testid="results-wins" />
          <Stat label="Losses" value={data.losses} accent="#A1A1AA" testid="results-losses" />
          <Stat label="Pushes" value={data.pushes} accent="#FACC15" testid="results-pushes" />
        </div>
      </div>

      {Object.keys(data.by_sport || {}).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A] mb-8">
          {Object.entries(data.by_sport).map(([sport, s]) => {
            const total = (s.wins || 0) + (s.losses || 0);
            const acc = total ? Math.round(((s.wins || 0) / total) * 100) : 0;
            return (
              <div key={sport} className="bg-[#0A0A0A] p-4" data-testid={`results-sport-${sport}`}>
                <div className="overline">{sport}</div>
                <div className="font-data text-2xl text-white mt-1">{acc}%</div>
                <div className="text-xs text-[#A1A1AA] mt-1">
                  {s.wins || 0}W · {s.losses || 0}L{s.pushes ? ` · ${s.pushes}P` : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="border border-[#27272A]" data-testid="results-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#121212] border-b border-[#27272A]">
              <th className="text-left p-3 overline">Result</th>
              <th className="text-left p-3 overline">Sport</th>
              <th className="text-left p-3 overline">Player / Game</th>
              <th className="text-left p-3 overline">Pick</th>
              <th className="text-right p-3 overline">Actual</th>
              <th className="text-right p-3 overline">Line</th>
              <th className="text-left p-3 overline hidden md:table-cell">When</th>
            </tr>
          </thead>
          <tbody>
            {(data.results || []).map((r) => (
              <tr
                key={r.prop_id}
                className="border-b border-[#1a1a1a] last:border-b-0 hover:bg-[#0f0f0f]"
                data-testid={`result-row-${r.prop_id}`}
              >
                <td className="p-3"><ResultBadge result={r.result} /></td>
                <td className="p-3 text-[#A1A1AA] font-data text-xs">{r.sport}</td>
                <td className="p-3 text-white">
                  <div>{r.player_name || r.matchup}</div>
                  <div className="text-xs text-[#A1A1AA]">{r.stat_type}</div>
                </td>
                <td className="p-3">
                  <span
                    className={`px-2 py-0.5 text-xs uppercase font-head tracking-wider ${r.pick === "OVER" ? "pick-over" : "pick-under"}`}
                  >
                    {r.pick} {r.line}
                  </span>
                </td>
                <td className="p-3 text-right font-data text-white">{r.actual != null ? r.actual : "—"}</td>
                <td className="p-3 text-right font-data text-[#A1A1AA]">{r.line}</td>
                <td className="p-3 text-[#A1A1AA] text-xs hidden md:table-cell">
                  {r.commence_time ? new Date(r.commence_time).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-[#52525b] mt-6">
        Results disappear automatically 48 hours after grading. Player-stat results sourced from ESPN; game-total
        results from The Odds API.
      </p>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-data text-2xl" style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}

function ResultBadge({ result }) {
  if (result === "WIN") return <span className="inline-flex items-center gap-1.5 text-[#22c55e] font-head"><CheckCircle size={16} weight="fill" />WIN</span>;
  if (result === "LOSS") return <span className="inline-flex items-center gap-1.5 text-[#A1A1AA] font-head"><XCircle size={16} weight="fill" />LOSS</span>;
  if (result === "PUSH") return <span className="inline-flex items-center gap-1.5 text-[#FACC15] font-head"><MinusCircle size={16} weight="fill" />PUSH</span>;
  return <span className="text-[#52525b]">—</span>;
}
